import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

import type { Customer } from '@/features/intelligence-dashboard/customer.schema';

import type { Referral, SubmitReferralInput } from './referrals-store';

/**
 * Referral API client (SCRUM-343 — Phase 2a wiring). Follows the established
 * fetch conventions: relative /api/* URL constants (tenant from the token claim), res.ok
 * guard, zod-validated responses. The store's workflow actions
 * (approve/release/reassign/deny/reopen) remain CLIENT-side until SCRUM-341
 * lands the workflow endpoints.
 */
const REFERRALS_URL = '/api/referrals';
// ↑ Swap-point: nginx proxies /api/* to bankiq-api in the deployed container.

// Tenant ID for the Meridian Community Bank tenant (seeded by bankiq-db-seed CE Job).
// SCRUM-643: tenant derived server-side from the App ID token claim (SCRUM-102).

const referralEventSchema = z.object({
  type: z.enum(['reassigned', 'released', 'denied', 'converted']),
  to: z.string(),
  by: z.string(),
  note: z.string().nullable(),
  daysAgo: z.number(),
});

const referralSummarySchema = z.object({
  id: z.string(),
  customerId: z.string(),
  customerName: z.string(),
  productId: z.string().nullable(),
  productName: z.string(),
  referrerName: z.string(),
  note: z.string(),
  submittedAt: z.string(),
  status: z.enum(['pending', 'reviewed', 'campaign_sent', 'denied']),
  receivingUserId: z.string().nullable(),
  receivingUserName: z.string(),
  // SCRUM-347 workflow surface
  events: referralEventSchema.array(),
  denialNote: z.string().nullable(),
  tellerUnread: z.boolean(),
  converted: z.boolean(),
  expired: z.boolean(),
});

export type ApiReferralSummary = z.infer<typeof referralSummarySchema>;

/** GET /api/referrals — the tenant's referrals, newest first. */
export async function fetchReferrals(): Promise<ApiReferralSummary[]> {
  const res = await apiFetch(REFERRALS_URL);
  if (!res.ok) {
    throw new Error(`GET ${REFERRALS_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const raw: unknown = await res.json();
  return referralSummarySchema.array().parse(raw);
}

/** POST /api/referrals — persists the referral; returns the server id. */
export async function postReferral(input: SubmitReferralInput): Promise<string> {
  const res = await apiFetch(REFERRALS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customerId: input.customerId,
      productId: input.productId || null,
      receivingUserId: input.assignee?.userId ?? null,
      note: input.note,
      // Demo-era referrer identity (matched server-side by email; JWT identity
      // replaces this with SCRUM-102).
      referrerEmail: input.submitterEmail ?? null,
    }),
  });
  if (!res.ok) {
    throw new Error(`POST ${REFERRALS_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const body = (await res.json()) as { id: string };
  return body.id;
}

/**
 * Maps an API referral row onto the store's Referral shape (SCRUM-325).
 * Branch resolves via the live customer record (the projection carries no
 * branch — routing is server-side in Phase 2b); assignees arrive WITHOUT
 * emails (PII-free wire), so `isMine` matching is name-blind for API rows
 * until SCRUM-102 introduces real identity.
 */
export function toStoreReferral(row: ApiReferralSummary, customers: readonly Customer[]): Referral {
  const customer = customers.find((c) => c.id === row.customerId);
  const submitted = Date.parse(row.submittedAt);
  const daysAgo = Number.isNaN(submitted)
    ? 0
    : Math.max(0, Math.floor((Date.now() - submitted) / 86_400_000));
  return {
    id: row.id,
    customerId: row.customerId,
    customerName: row.customerName,
    productId: row.productId ?? '',
    productName: row.productName,
    tellerName: row.referrerName,
    branch: customer?.branch ?? 'Main',
    note: row.note,
    submittedDaysAgo: daysAgo,
    status: row.status,
    assignee: row.receivingUserId
      ? { name: row.receivingUserName, email: '', role: 'Teammate', userId: row.receivingUserId }
      : null,
    events: row.events.map((e) => ({
      type: e.type,
      by: e.by,
      daysAgo: e.daysAgo,
      ...(e.to ? { to: e.to } : {}),
      ...(e.note ? { note: e.note } : {}),
    })),
    tellerUnread: row.tellerUnread,
    converted: row.converted,
    ...(row.denialNote ? { denialNote: row.denialNote } : {}),
  };
}

/** Workflow endpoint actions (SCRUM-348) — POST /api/referrals/{id}/{action}. */
export type ReferralWorkflowAction =
  | 'approve'
  | 'review'
  | 'campaign-sent'
  | 'deny'
  | 'reopen'
  | 'assign'
  | 'release'
  | 'read';

/**
 * Fire a workflow action server-side. Fire-and-forget from the store: local
 * state mutates immediately for instant UX; failures are swallowed so the
 * offline preview keeps working (the server round-trip is verified post-merge
 * against dev). SCRUM-102 replaces actorEmail with the JWT identity.
 */
export async function postReferralAction(
  id: string,
  action: ReferralWorkflowAction,
  body: Record<string, unknown> = {},
): Promise<void> {
  const res = await apiFetch(`${REFERRALS_URL}/${id}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`POST ${action} failed: ${String(res.status)} ${res.statusText}`);
  }
}
